import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    NgxScannerQrcodeComponent,
    NgxScannerQrcodeService,
    ScannerQRCodeDevice,
    ScannerQRCodeSelectedFiles,
} from 'ngx-scanner-qrcode';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EncryptService } from '../../core/services/encrypt/encrypt.service';
import { QrisDecodeUsecase } from '../../core/usecase/fs-wallet-qris/qris-decode.usecase';

@Component({
    selector: 'wallet-qris-interface',
    templateUrl: './wallet-qris-interface.component.html',
    styleUrls: ['./wallet-qris-interface.component.scss'],
    //   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalletQrisInterfaceComponent implements AfterViewInit {
    scannerEnabled = true;
    data: any = null;
    lang: string = 'id';

    isLoading: boolean = false;
    token: string = '';
    isInvalidQRIS = false;
    destroyed$ = new Subject();
    errorMessageShown: boolean = false;
    errorMessageContent: string = '';
    private readonly ngUnsubsribe$ = new Subject<void>();
    devices: ScannerQRCodeDevice[] = [];
    selectedDeviceId: string | null = null;

    // Flash and Upload features
    hasFlashSupport: boolean = false;
    isFlashOn: boolean = false;
    showGalleryPermissionDenied: boolean = false;
    showQRNotFound: boolean = false;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    config = {
        fps: 24,
        isBeep: false,
        constraints: {
            video: {
                facingMode: 'environment', // back camera
                // height: window.innerWidth,
                // width: { ideal: 1920 },
                height: { ideal: 1080 },
                frameRate: { ideal: 24, max: 30 },
                // width: window.innerHeight * 0.8,
                focusMode: 'continuous',
            },
            advanced: [{ focusMode: 'continuous' }, { zoom: 1.5 }],
        },
        canvasStyles: [
            { lineWidth: 1, strokeStyle: 'green', fillStyle: '#55f02880' },
            { font: '0px serif', strokeStyle: '#fff0', fillStyle: '#ff0000' },
        ],
    };

    @ViewChild('action') action!: NgxScannerQrcodeComponent;

    constructor(
        private readonly activeRouter: ActivatedRoute,
        private readonly qrDecodeUsecase: QrisDecodeUsecase,
        private readonly messageService: MessageService,
        private readonly translateService: TranslateService,
        private readonly encryptService: EncryptService,
        private readonly qrService: NgxScannerQrcodeService,
        public route: Router,
    ) {
        this.activeRouter.queryParamMap.subscribe((params) => {
            this.token = params.get('token')!;
            this.lang = params.get('lang')!;
            if (this.token) {
                this.data = this.getJwtPayload(this.token);
            }
        });
    }

    ngOnInit(): void {
        // if (!this.data) {
        //   this.route.navigate(['']);
        // } else {
        //   this.onScanResult('onScanResult');
        // }
        // scanner.start
        // this.onScanResult('00020101021126760024ID.CO.SPEEDCASH.MERCHANT01189360081530001108500215ID10210011085060303UBE51440014ID.CO.QRIS.WWW0215ID20211101828900303UBE5204829953033605802ID5908DINNCAFE6008SURABAYA61056029462260506S155880105280940703A0163041E7B');
    }

    ngAfterViewInit() {
        this.action.isReady.subscribe((res: any) => {
            this.handle(this.action, 'start');

            // Check flash support after camera starts
            this.checkFlashSupport();
        });

        // ✅ Subscribe to device list
        this.action.devices.subscribe((devices) => {
            this.devices = devices;
            // console.table(devices);

            if (devices.length > 0 && !this.selectedDeviceId) {
                // Try to auto-select main back camera
                const backCam =
                    devices.find(
                        (d) => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('rear'),
                    ) || devices[0];

                this.selectedDeviceId = backCam.deviceId;

                // await this.startScanner(backCam.deviceId);
            }
        });

        //  await this.startScanner(this.selectedDeviceId || '');
    }

    getJwtPayload(t: string) {
        let payload;
        try {
            payload = JSON.parse(window.atob(t.split('.')[1]));
        } catch (error) {
            // do nothing
            return null;
        }
        return payload;
    }

    public handle(action: any, fn: string): void {
        const playDeviceFacingBack = (devices: any[]) => {
            // Find all back/rear cameras
            const backCameras = devices.filter(
                (d) => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('rear'),
            );
            // front camera or back camera check here!
            const device = devices.find((f) => /back|rear|environment/gi.test(f.label));

            // Heuristic: prefer macro/tele/numbered camera
            const preferred =
                backCameras.find((d) => /(macro|tele|1|2|3|pro)/i.test(d.label)) || backCameras[0] || devices[0];

            // console.log(preferred);

            if (preferred) {
                action.playDevice(preferred.deviceId);
            } else {
                // Default Back Facing Camera
                action.playDevice(device ? device.deviceId : devices[0].deviceId);
            }
        };

        if (fn === 'start') {
            action[fn](playDeviceFacingBack).subscribe((r: any) => console.log(fn, r), alert);
        } else {
            action[fn]().subscribe((r: any) => console.log(fn, r), alert);
        }
    }

    private checkFlashSupport(): void {
        const videoEl: HTMLVideoElement | undefined = this.action.video?.nativeElement;

        if (!videoEl) {
            console.log('[Flash] No video element found');
            return;
        }

        // If stream already active, check immediately
        if (videoEl.srcObject) {
            this.detectFlashSupport(videoEl.srcObject as MediaStream);
            return;
        }

        // srcObject is a JS property, MutationObserver can't detect it.
        // Poll until the stream is available.
        console.log('[Flash] Waiting for camera stream...');
        let attempts = 0;
        const poll = setInterval(() => {
            attempts++;
            if (videoEl.srcObject) {
                clearInterval(poll);
                this.detectFlashSupport(videoEl.srcObject as MediaStream);
            } else if (attempts >= 20) {
                clearInterval(poll);
                console.log('[Flash] Timed out waiting for camera stream');
            }
        }, 300);
    }

    private detectFlashSupport(stream: MediaStream): void {
        if (this.hasFlashSupport) return;

        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack) {
            console.log('[Flash] No video track in stream');
            return;
        }

        const capabilities = videoTrack.getCapabilities();
        this.hasFlashSupport = 'torch' in capabilities;
        console.log(`[Flash] Flash supported: ${this.hasFlashSupport}`);
    }

    toggleFlash(): void {
        const stream = this.action.video?.nativeElement.srcObject as MediaStream;
        const videoTrack = stream?.getVideoTracks()[0];
        console.log('[Flash] Toggle tapped, isFlashOn:', this.isFlashOn, 'track readyState:', videoTrack?.readyState);

        if (!videoTrack) {
            console.error('[Flash] No video track available for toggle');
            return;
        }

        const newState = !this.isFlashOn;
        console.log(`[Flash] Applying torch: ${newState}`);

        videoTrack
            .applyConstraints({ advanced: [{ torch: newState } as any] })
            .then(() => {
                this.isFlashOn = newState;
                // Verify the constraint was actually applied
                const settings = videoTrack.getSettings();
                console.log('[Flash] Track settings after apply:', settings);
                console.log(`[Flash] Torch actually applied: ${(settings as any).torch}`);
            })
            .catch((err: any) => {
                console.error('[Flash] Failed to toggle torch:', err);
            });
    }

    private turnOffFlash(): void {
        if (!this.isFlashOn) return;

        const videoTrack = (this.action.video?.nativeElement.srcObject as MediaStream)?.getVideoTracks()[0];
        if (videoTrack) {
            videoTrack
                .applyConstraints({ advanced: [{ torch: false } as any] })
                .then(() => {
                    this.isFlashOn = false;
                })
                .catch(() => {});
        }
    }

    triggerFileUpload(): void {
        this.fileInput?.nativeElement?.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];

        if (!file) {
            return;
        }

        // Pause scanner while processing
        this.turnOffFlash();
        this.handle(this.action, 'pause');
        this.isLoading = true;

        this.qrService.loadFilesToScan([file], this.config).subscribe({
            next: (results: ScannerQRCodeSelectedFiles[]) => {
                const result = results[0];
                if (result && result.data && result.data.length > 0) {
                    const qrValue = result.data[0].value;
                    console.log('Scanned QR Code from image:', qrValue);

                    const isQRISValid = this.verifyQris(qrValue);
                    if (isQRISValid) {
                        this.decodeQr(qrValue);
                    } else {
                        this.isLoading = false;
                        this.isInvalidQRIS = true;
                    }
                } else {
                    this.isLoading = false;
                    this.showQRNotFound = true;
                }
                // Reset file input
                if (input) {
                    input.value = '';
                }
            },
            error: (err: any) => {
                console.error('QR scan from image error:', err);
                this.isLoading = false;
                this.showQRNotFound = true;
                // Reset file input
                if (input) {
                    input.value = '';
                }
            },
        });
    }

    openDeviceSettings(): void {
        this.showGalleryPermissionDenied = false;
        // Show alert with instructions since we can't directly open device settings
        alert(this.translateService.instant('wallet_qris.gallery_permission_desc'));
    }

    clearQRNotFoundState(): void {
        this.showQRNotFound = false;
        this.handle(this.action, 'play');
    }

    onScanResult(events: any): void {
        this.turnOffFlash();
        this.handle(this.action, 'pause');
        console.log('Scanned QR Code:', events[0].value);
        const isQRISValid = this.verifyQris(events[0].value);
        if (isQRISValid) {
            this.decodeQr(events[0].value);
        } else {
            this.isInvalidQRIS = true;
        }
    }

    verifyQris(qr: string) {
        // START WITH 00
        const hasValidStartTag = qr.startsWith('00');

        // Check for TLV Format Consistency
        const hasValidTLV = this.isValidQRISTLV(qr);
        // Contain string "ID.CO.QRIS.WWW" or "ID.GPNQR"
        const hasAuthorizedRef = qr.includes('ID.CO.QRIS.WWW') || qr.includes('ID.');
        // Has CRC TAG last 8 digit start with 6304
        const hasCRCTag = qr.slice(-8).includes('6304');
        // Verify CRC last 4 digit equal calculated CRC
        const hasValidCRC = this.calculateCRC16CCITT(qr.slice(0, -4)) === qr.slice(-4);
        // contain either 0303UKE, 0303UMI, 0303UME, 0303UBE, 0303URE
        const MERCHANT_TYPES = ['0303UKE', '0303UMI', '0303UME', '0303UBE', '0303URE'];
        const hasMerchantType = MERCHANT_TYPES.some((type) => qr.includes(type));

        return hasValidStartTag && hasValidTLV && hasAuthorizedRef && hasCRCTag && hasValidCRC && hasMerchantType;
    }

    // Calculate CRC
    calculateCRC16CCITT(input: string) {
        let crc = 0xffff;
        for (let i = 0; i < input.length; i++) {
            crc ^= input.charCodeAt(i) << 8;
            for (let j = 0; j < 8; j++) {
                if (crc & 0x8000) {
                    crc = (crc << 1) ^ 0x1021;
                } else {
                    crc <<= 1;
                }
                crc &= 0xffff;
            }
        }
        return crc.toString(16).toUpperCase().padStart(4, '0');
    }

    isValidQRISTLV(input: string): boolean {
        let index = 0;

        while (index < input.length) {
            // Ensure at least tag + length
            if (index + 4 > input.length) return false;

            const tag = input.slice(index, index + 2);
            const lenStr = input.slice(index + 2, index + 4);
            const length = parseInt(lenStr, 10);

            if (isNaN(length) || length < 0) return false;

            const valueStart = index + 4;
            const valueEnd = valueStart + length;

            // Check if value fits within the string
            if (valueEnd > input.length) return false;

            const value = input.slice(valueStart, valueEnd);

            // Stop early if tag is 63 (CRC) — it must be the last tag
            if (tag === '63') {
                if (length !== 4 || valueEnd !== input.length) return false;
                break;
            }

            index = valueEnd;
        }

        return true;
    }

    async decodeQr(qr: string) {
        this.isLoading = true;
        const data = this.generateQrPayload(qr);
        this.qrDecodeUsecase
            .execute(await data)
            .pipe(takeUntil(this.ngUnsubsribe$))
            .subscribe({
                next: (res) => {
                    this.isLoading = false;
                    console.log('test');
                    this.route.navigate(['/qris-detail'], {
                        state: {
                            token: this.token,
                            data: res.data,
                            qr_content: qr,
                            token_data: this.data,
                        },
                        queryParams: { lang: this.lang },
                        skipLocationChange: true,
                    });
                },
                error: (err) => {
                    this.isLoading = false;
                    this.isInvalidQRIS = true;

                    const error_detail = this.translateService.instant('messages.error');
                    this.messageService.add({
                        severity: 'error',
                        detail: error_detail,
                    });
                },
            });
    }

    async encryptData(qrData: any) {
        const result = this.encryptService.encryptHybridPayload(qrData, environment.public_pem);
        return result;
    }

    async generateQrPayload(qr: string) {
        const qrData = {
            qr_content: qr,
            scan_time: new Date().toISOString(),
        };
        const encrypted_payload = await this.encryptData(qrData);
        return {
            company_id: this.data.client_ref_id,
            user_id: this.data.user_ref_id,
            wallet_id: this.data.wallet_id,
            token: this.token,
            ...encrypted_payload,
        };
    }

    clearState() {
        this.isInvalidQRIS = false;
        this.showQRNotFound = false;
        this.showGalleryPermissionDenied = false;
        this.handle(this.action, 'play');
    }
}
